import { z } from 'zod';
import { ServiceType } from '../types/enums';

/**
 * AB#1675 Phase A — Business Details form schema.
 *
 * The full form on `/my-business/business-details` includes some fields the
 * BE supports today (Name, About, ServiceType / "Salon Type", Country,
 * BusinessHours) and several that don't have a BE column yet (Holiday
 * Closures, Social Links, Multi-type, Decimal Value, the three booking
 * toggles, NameAR / AboutAR, Email/Phone/Website, Logo). The deferred
 * fields are validated here so the local-draft path (localStorage) is
 * type-safe end-to-end and the Phase B BE wiring is a pure swap from
 * "save to localStorage" → "PUT to /api/v2/business/details" with the
 * same shape.
 */

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/* ── Per-day business hours ─────────────────────────────────────────────── */

export const businessDayHoursSchema = z.object({
    /** 0 = Sunday … 6 = Saturday (matches BE BusinessHours.DayOfWeek). */
    dayOfWeek: z.number().int().min(0).max(6),
    isOpen: z.boolean(),
    /** "HH:MM" 24-hour. Optional only when day is closed. */
    openTime: z.string().regex(HHMM, 'Invalid time').optional(),
    closeTime: z.string().regex(HHMM, 'Invalid time').optional(),
    is24Hours: z.boolean().default(false),
}).refine(
    (d) => !d.isOpen || (!!d.openTime && !!d.closeTime) || d.is24Hours,
    { message: 'Open days must have both open and close times', path: ['openTime'] },
);

/* ── Holiday closure (single day or date range) ─────────────────────────── */

export const holidayClosureSchema = z.object({
    id: z.string().min(1),
    from: z.string().regex(ISO_DATE, 'Invalid date'),
    to: z.string().regex(ISO_DATE, 'Invalid date'),
    reason: z.string().max(200).default(''),
}).refine(
    (c) => c.to >= c.from,
    { message: 'End date must be on or after start date', path: ['to'] },
);

/* ── Social links — all optional ────────────────────────────────────────── */

export const socialLinksSchema = z.object({
    facebook: z.string().max(300).optional().default(''),
    twitter: z.string().max(300).optional().default(''),
    instagram: z.string().max(300).optional().default(''),
    whatsapp: z.string().max(60).optional().default(''),
    tiktok: z.string().max(300).optional().default(''),
    googleMaps: z.string().max(500).optional().default(''),
});

/* ── Other settings ─────────────────────────────────────────────────────── */

export const otherSettingsSchema = z.object({
    /** "Both" | "male" | "female" — kept loose for the FE picker. Maps to
     *  ServiceType (Everyone/MenOnly/WomenOnly) when sent to BE. */
    salonType: z.enum(['both', 'male', 'female']).optional().default('both'),
    decimalDigits: z.union([z.literal(0), z.literal(2), z.literal(3)]).default(2),
    autoConfirmBookings: z.boolean().default(true),
    allowWalkIns: z.boolean().default(false),
    showRatingsPublicly: z.boolean().default(true),
});

/* ── Top-level form schema ──────────────────────────────────────────────── */

export const businessDetailsFormSchema = z.object({
    /* Persisted to BE today (Phase A). */
    name: z.string().trim().min(2, 'Business name is required').max(200),
    /**
     * CR-2026-05-31-c (AB#1826) — About is now MANDATORY in BOTH
     * languages with a 50-char min. EN-only error strings here match the
     * existing inline pattern in this file (the page isn't fully i18n'd
     * yet; full migration is tracked as a follow-up cleanup PBI). AR
     * counter + asterisk live in BusinessDetailsSection.
     */
    about: z.string().trim()
        .min(50, 'About your business is required (min 50 characters)')
        .max(2000, 'About is too long (max 2000 characters)'),
    /** ISO-2 country code (AE, JO, …). Stored as string on BE.country. */
    country: z.string().trim().min(2, 'Country is required').max(2),
    serviceType: z.nativeEnum(ServiceType).default(ServiceType.Everyone),
    businessHours: z.array(businessDayHoursSchema).length(7),

    /* Persisted locally (Phase B will move these to BE). */
    /**
     * CR-2026-05-31-a (AB#1825) — Arabic business name is now MANDATORY.
     * Same min-length floor as EN `name` (2 chars). Inline EN error
     * matches the existing pattern in this file.
     */
    nameAr: z.string().trim()
        .min(2, 'Arabic business name is required')
        .max(200, 'Arabic name is too long (max 200 characters)'),
    /**
     * CR-2026-05-31-c (AB#1826) — AR about MANDATORY, 50-char min.
     */
    aboutAr: z.string().trim()
        .min(50, 'نبذة بالعربية مطلوبة (50 حرف على الأقل) / Arabic about is required (min 50 chars)')
        .max(2000, 'النبذة طويلة جداً / Arabic about is too long'),
    email: z.string().trim().max(150).optional().default('').refine(
        (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
        { message: 'Invalid email address' },
    ),
    phone: z.string().trim().max(40).optional().default(''),
    website: z.string().trim().max(300).optional().default(''),
    /** 1–4 selected business types (Hair salon, Spa, Barbershop, …). */
    businessTypes: z.array(z.string()).min(0).max(4).default([]),
    holidays: z.array(holidayClosureSchema).default([]),
    social: socialLinksSchema.default({}),
    settings: otherSettingsSchema.default({}),
});

/* RHF works with the schema's INPUT type (pre-defaults), zodResolver
 * produces the OUTPUT type. We expose both so consumers can pick. The
 * `BusinessDetailsForm` alias points at the OUTPUT type so callers see
 * fully-populated values; the form component itself uses `z.input` when
 * declaring `useForm` to keep the resolver type happy. */
export type BusinessDetailsForm = z.output<typeof businessDetailsFormSchema>;
export type BusinessDetailsFormInput = z.input<typeof businessDetailsFormSchema>;
export type BusinessDayHoursForm = z.output<typeof businessDayHoursSchema>;
export type HolidayClosureForm = z.output<typeof holidayClosureSchema>;
export type SocialLinksForm = z.output<typeof socialLinksSchema>;
export type OtherSettingsForm = z.output<typeof otherSettingsSchema>;

/* ── Defaults ───────────────────────────────────────────────────────────── */

export const defaultDayHours = (dayOfWeek: number): BusinessDayHoursForm => ({
    dayOfWeek,
    isOpen: true,
    openTime: '09:00',
    closeTime: '21:00',
    is24Hours: false,
});

export const defaultBusinessDetailsForm = (): BusinessDetailsForm => ({
    name: '',
    // CR-2026-05-31-c: about/aboutAr are now z.string with min(50). Defaults
    // are still empty strings to allow the form to render in empty state;
    // submit will fail validation until min length met.
    about: '',
    country: 'AE',
    serviceType: ServiceType.Everyone,
    businessHours: Array.from({ length: 7 }, (_, i) => defaultDayHours(i)),
    // CR-2026-05-31-a: nameAr now z.string with min(2).
    nameAr: '',
    aboutAr: '',
    email: '',
    phone: '',
    website: '',
    businessTypes: [],
    holidays: [],
    social: {
        facebook: '', twitter: '', instagram: '', whatsapp: '', tiktok: '', googleMaps: '',
    },
    settings: {
        salonType: 'both',
        decimalDigits: 2,
        autoConfirmBookings: true,
        allowWalkIns: false,
        showRatingsPublicly: true,
    },
});

/* ── ServiceType ↔ Salon Type select mapping ────────────────────────────── */

export const SALON_TYPE_TO_SERVICE_TYPE: Record<OtherSettingsForm['salonType'], ServiceType> = {
    both: ServiceType.Everyone,
    male: ServiceType.MenOnly,
    female: ServiceType.WomenOnly,
};

export const SERVICE_TYPE_TO_SALON_TYPE: Record<ServiceType, OtherSettingsForm['salonType']> = {
    [ServiceType.Everyone]: 'both',
    [ServiceType.MenOnly]: 'male',
    [ServiceType.WomenOnly]: 'female',
};
